import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Plug, Upload, X } from "lucide-react";
import ModalScrim from "./ModalScrim";
import { Badge } from "../lib/ui";
import { runConnector, runConnectorLive } from "../lib/api";
import { connectorDeliveryHref, connectorIntakeHref } from "../lib/connectors";

export default function ConnectorActionModal({ connectorId, appId, onClose, onImportReplay }) {
  const [busy, setBusy] = useState(true);
  const [liveBusy, setLiveBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    setErr("");
    runConnector(connectorId)
      .then((r) => { if (!cancelled) setResult(r.data); })
      .catch((ex) => {
        if (!cancelled) setErr(ex.response?.data?.detail || ex.message || "Connector request failed");
      })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [connectorId]);

  const connector = result?.connector;
  const isSource = connector?.kind === "source" || connector?.action === "read";
  const replay = result?.import_replay;

  return (
    <ModalScrim testId={`connector-modal-${connectorId}`}>
      <div className="modal wide connector-modal">
        <button type="button" className="modal-close" onClick={onClose} aria-label="Close"><X size={18} /></button>
        <p className="kicker">CONNECTOR · {connector?.label || connectorId}</p>
        <h2>{isSource ? "Read authoritative facts" : "Feed outcome evidence"}</h2>

        {busy && <p className="muted" data-testid="connector-modal-loading">Checking connector status…</p>}
        {err && <div className="error-message" data-testid="connector-modal-error">{err}</div>}

        {!busy && result && (
          <>
            <div className="connector-status-row">
              <Badge tone={result.live ? "green" : "amber"}>{result.live ? "LIVE" : "STUB · NEXT"}</Badge>
              <Badge tone="slate">Model=None</Badge>
              <span className="muted small-note">collection_status: {result.collection_status}</span>
            </div>
            <p className="muted">{result.message}</p>
            {connector?.note && <p className="small-note">{connector.note}</p>}

            <div className="connector-modal-actions">
              {appId && connector?.live_available && isSource && (
                <button
                  type="button"
                  className="primary-button"
                  data-testid="connector-live-pull"
                  disabled={liveBusy}
                  onClick={async () => {
                    setLiveBusy(true);
                    setErr("");
                    try {
                      const { data } = await runConnectorLive(connectorId, { app_id: appId });
                      onImportReplay?.({
                        connector,
                        replay: { channel: "live", sample_csv: "" },
                        result: data,
                        liveDone: true,
                      });
                      onClose();
                    } catch (ex) {
                      setErr(ex.response?.data?.detail || ex.message || "Live pull failed");
                    } finally {
                      setLiveBusy(false);
                    }
                  }}
                >
                  <Plug size={15} /> {liveBusy ? "Pulling…" : "Live pull into evidence"}
                </button>
              )}
              {appId && replay && (
                <button
                  type="button"
                  className="primary-button"
                  data-testid="connector-import-replay"
                  onClick={() => onImportReplay?.({ connector, replay, result })}
                >
                  <Upload size={15} /> Import replay ({replay.channel?.toUpperCase()})
                </button>
              )}
              {appId && (
                <Link
                  to={isSource ? connectorIntakeHref(appId, connectorId) : connectorDeliveryHref(appId, connectorId)}
                  className="secondary-button"
                  data-testid="connector-open-target"
                  onClick={onClose}
                >
                  {isSource ? "Open evidence sources" : "Open delivery value"} <ChevronRight size={14} />
                </Link>
              )}
              {!appId && (
                <p className="muted small-note">Select an application from Portfolio or Cockpit to import evidence.</p>
              )}
            </div>

            {replay && (
              <div className="connector-replay-hint">
                <Plug size={14} />
                <span>
                  POC path: export from {connector?.label} → import with source <em>{replay.source_label}</em>.
                  Live zero-copy connectors are Next.
                </span>
              </div>
            )}
          </>
        )}

        <button type="button" className="text-button" onClick={onClose}>Close</button>
      </div>
    </ModalScrim>
  );
}
